import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true; // Track mute state (default muted)

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (playIcon) {
        playIcon.classList.toggle('hidden', !isPlaying);
      }
      if (pauseIcon) {
        pauseIcon.classList.toggle('hidden', isPlaying);
      }
    }
  }

  /**
   * Updates the visual hint for mute/unmute state
   * @param {boolean} isMuted - Whether the video is currently muted
   */
  updateMuteHint(isMuted) {
    const toggleMuteButton = this.refs.toggleMuteButton;
    if (toggleMuteButton instanceof HTMLElement) {
      const muteIcon = toggleMuteButton.querySelector('.icon-mute');
      if (muteIcon) muteIcon.classList.toggle('hidden', !isMuted);
      const unmuteIcon = toggleMuteButton.querySelector('.icon-unmute');
      if (unmuteIcon) unmuteIcon.classList.toggle('hidden', isMuted);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    const video = this.querySelector('video');
    const iframe = this.querySelector('iframe[data-video-type]');

    if (video) {
      if (video.paused) {
        this.playMedia();
      } else {
        this.pauseMedia();
      }
    } else if (iframe) {
      if (this.isPlaying) {
        this.pauseMedia();
      } else {
        this.playMedia();
      }
    }
  }


  /**
   * Toggle mute/unmute state of the media
   */
  toggleMute() {
    this.isMuted = !this.isMuted;
    const video = this.querySelector('video');
    if (video) {
      video.muted = this.isMuted;
    }
    this.updateMuteHint(this.isMuted);

    if (!this.isMuted) {
      document.querySelectorAll('deferred-media').forEach((other) => {
        if (other !== this && !other.isMuted) {
          other.isMuted = true;
          const otherVideo = other.querySelector('video');
          if (otherVideo) otherVideo.muted = true;
          other.updateMuteHint(true);
        }
      });
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    } else {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

class AudioPlayer extends HTMLElement {
  constructor() {
    super();
    this.audio = null;
    this.button = null;
    this.playIcon = null;
    this.pauseIcon = null;
    this.playText = null;
  }

  connectedCallback() {
    this.audio = this.querySelector('audio');
    this.button = this.querySelector('.play-audio-button');
    this.playIcon = this.querySelector('.icon-play');
    this.pauseIcon = this.querySelector('.icon-pause');
    this.playText = this.querySelector('.play-text');

    if (!this.audio || !this.button) {
      console.error('Audio or button element not found');
      return;
    }

    this.button.addEventListener('click', this.toggleAudio.bind(this));
    this.audio.addEventListener('play', this.handlePlay.bind(this));
    this.audio.addEventListener('pause', this.handlePause.bind(this));
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.toggleAudio.bind(this));
    }
    if (this.audio) {
      this.audio.removeEventListener('play', this.handlePlay.bind(this));
      this.audio.removeEventListener('pause', this.handlePause.bind(this));
    }
  }

  toggleAudio() {
    if (!this.audio) return;

    if (this.audio.paused) {
      this.audio.play();
    } else {
      this.audio.pause();
    }
  }

  handlePlay() {
    if (this.playIcon) this.playIcon.classList.add('hidden');
    if (this.pauseIcon) this.pauseIcon.classList.remove('hidden');
    if (this.playText) this.playText.textContent = 'PAUSE AUDIO';
  }

  handlePause() {
    if (this.playIcon) this.playIcon.classList.remove('hidden');
    if (this.pauseIcon) this.pauseIcon.classList.add('hidden');
    if (this.playText) this.playText.textContent = 'PLAY AUDIO';
  }
}

if (!customElements.get('audio-player')) {
  customElements.define('audio-player', AudioPlayer);
}